import type { InputHTMLAttributes, Ref, TextareaHTMLAttributes } from 'react';

import { CONTROL } from './control';
import { cx } from './cx';
import { useInvalid } from './fieldProblem';

export function Input({
  className,
  ref,
  id,
  onChange,
  'aria-describedby': describedBy,
  ...rest
}: {
  className?: string;
  /** React 19 passes a ref as an ordinary prop; the order form focuses the number box. */
  ref?: Ref<HTMLInputElement>;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>) {
  const invalid = useInvalid(id, describedBy, onChange);

  return (
    <input
      {...rest}
      {...invalid}
      id={id}
      ref={ref}
      className={cx(CONTROL, 'placeholder:text-ink-3', className)}
    />
  );
}

/**
 * The one control that is not 32px tall, for the few places a value is genuinely a list or a
 * paragraph — a firm's inbound numbers, a driver's address. Everything a dispatcher types
 * while somebody is talking is an `Input`, because a row of 32px controls is what makes a
 * form readable at a glance.
 */
export function Textarea({
  className,
  rows = 2,
  ref,
  id,
  onChange,
  'aria-describedby': describedBy,
  ...rest
}: {
  className?: string;
  ref?: Ref<HTMLTextAreaElement>;
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>) {
  const invalid = useInvalid(id, describedBy, onChange);

  return (
    <textarea
      {...rest}
      {...invalid}
      id={id}
      ref={ref}
      rows={rows}
      className={cx(
        'w-full px-2.5 py-1.5 rounded-control bg-surface-2 border border-rule text-body text-ink placeholder:text-ink-3 resize-none aria-invalid:border-danger',
        className,
      )}
    />
  );
}
